import { find, filter, is } from 'ramda';

type SingleOrArray<T> = T | T[];
type Kvp<K, V> = { key: K; value: V };

interface IProvider<T> {
    getAll(): T[];
}

export interface IKvpProvider<T> extends IProvider<T> {
    add(elem: SingleOrArray<Kvp<string, T>>): void;
    add(key: string, value: T): void;
    remove(key: string | T): T | undefined;
    update(value: Kvp<string, T>): void;
    update(key: string, value: T): void;
    getAll(predicate?: (elem: T) => boolean): T[];
    get(key: string): T | undefined;
    get(predicate: (elem: T) => boolean): T | undefined;
    has(key: string): boolean;
    has(predicate: (elem: T) => boolean): boolean;
}

export class KvpProvider<T> implements IKvpProvider<T> {
    private store: Map<string, T> = new Map();

    add(elem: SingleOrArray<Kvp<string, T>>): void;
    add(key: string, value: T): void;
    add(elemOrKey: any, value?: T): void {
        if (Array.isArray(elemOrKey)) {
            elemOrKey.forEach((elem) => {
                this.store.set(elem.key, elem.value);
            });
        } else if (is(Object, elemOrKey) && 'key' in elemOrKey && 'value' in elemOrKey) {
            this.store.set((elemOrKey as Kvp<string, T>).key, (elemOrKey as Kvp<string, T>).value);
        } else {
            this.store.set(elemOrKey, value as T);
        }
    }

    remove(keyOrValue: string | T): T | undefined {
        if (is(String, keyOrValue)) {
            const value = this.store.get(keyOrValue as string);
            this.store.delete(keyOrValue as string);
            return value;
        } else {
            for (let [key, value] of this.store.entries()) {
                if (value === keyOrValue) {
                    this.store.delete(key);
                    return value;
                }
            }
        }
        return undefined;
    }

    update(value: Kvp<string, T>): void;
    update(key: string, value: T): void;
    update(keyOrValue: any, value?: T): void {
        if (is(Object, keyOrValue) && !is(Array, keyOrValue)) {
            this.store.set(keyOrValue.key, keyOrValue.value);
        } else {
            this.store.set(keyOrValue as any, value as T);
        }
    }

    getAll(predicate?: (elem: T) => boolean): T[] {
        const values = Array.from(this.store.values());
        return predicate ? filter(predicate, values) : values;
    }

    get(keyOrPredicate: string | ((elem: T) => boolean)): T | undefined {
        if (is(String, keyOrPredicate)) {
            return this.store.get(keyOrPredicate as string);
        } else {
            return find(keyOrPredicate as (elem: T) => boolean, Array.from(this.store.values()));
        }
    }

    has(keyOrPredicate: string | ((elem: T) => boolean)): boolean {
        if (is(String, keyOrPredicate)) {
            return this.store.has(keyOrPredicate as string);
        } else {
            return !!find(keyOrPredicate as (elem: T) => boolean, Array.from(this.store.values()));
        }
    }
}
